'use client';
/**
 * HeaderAuth — 새 헤더 우측의 «사용자별» 영역 (클라이언트 섬).
 *
 * §0-5 서버 렌더 HTML 은 로그인/비로그인·봇/사람 모두 «동일» 해야 한다.
 *   → AuthProvider 의 loading 이 true 인 동안(= SSR 과 첫 hydrate)은 같은 크기의 빈 자리(72×40)만 그린다.
 *     로그인 판정이 브라우저에서 끝난 뒤에만 로그인 버튼 / 알림·아바타로 바뀐다. 자리 크기가 같아 CLS 0.
 *   → cookies()/headers() 를 읽지 않는다. AuthProvider 는 (main) 레이아웃에서 serverLoggedIn=false 고정.
 *
 * 로그인 링크는 Navigation 과 «같은 앵커»: navLoginHref(pathname,'nav') + trackNavLoginClick
 * (cta_click / nav_login_button). 카카오 로그인 버튼·세션 처리는 /login 과 AuthProvider 소관 — 여기서 건드리지 않는다.
 * 알림 개수는 표시만 한다(unread prop). 조회·폴링은 기존 Navigation 이 하고 있다 — 두 벌 폴링을 만들지 않는다.
 */
import Link from 'next/link';
import { Bell } from 'lucide-react';
import { useAuth } from '@/components/AuthProvider';
import { navLoginHref, trackNavLoginClick } from '@/components/nav/nav-login';

export interface HeaderAuthProps {
  pathname: string;
  /** 읽지 않은 알림 수(호출부가 이미 가진 값). 없으면 뱃지 없음. */
  unread?: number;
}

export default function HeaderAuth({ pathname, unread = 0 }: HeaderAuthProps) {
  const { userId, loading, profile } = useAuth();

  if (loading) {
    return (
      <span className="e-header__auth">
        <span className="e-header__authph" aria-hidden="true" />
      </span>
    );
  }

  if (!userId) {
    return (
      <span className="e-header__auth">
        <Link href={navLoginHref(pathname, 'nav')} onClick={trackNavLoginClick} className="e-btn-primary">
          로그인
        </Link>
      </span>
    );
  }

  const initial = (profile?.nickname ?? 'U').slice(0, 1).toUpperCase();
  return (
    <span className="e-header__auth">
      <Link href="/notifications" className="e-iconbtn e-header__desk" aria-label={unread > 0 ? `알림 ${unread}건` : '알림'}>
        <Bell size={18} strokeWidth={2} className="e-icon" aria-hidden="true" />
        {unread > 0 ? <span className="e-iconbtn__badge" aria-hidden="true">{unread > 9 ? '9+' : unread}</span> : null}
      </Link>
      <Link href={`/profile/${userId}`} className="e-header__avatar" aria-label="내 프로필">
        {profile?.avatarUrl ? <img src={profile.avatarUrl} alt="" width={40} height={40} /> : <span aria-hidden="true">{initial}</span>}
      </Link>
    </span>
  );
}
